import { Shield, Lock, MessageSquare, Database, Eye, Trash2, Mail, Phone } from "lucide-react";

const LAST_UPDATED = "September 29, 2026";
const CONTACT_EMAIL = "privacy@appointmentflo.com";
const COMPANY_NAME = "Digital Empire Marketing LLC";
const PLATFORM_NAME = "Appointment Flo";
const PLATFORM_URL = "https://appointmentflo.com";

export default function PrivacyPolicy() {
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-[#273240] text-white py-6 px-6">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center gap-3 mb-2">
            <Shield size={28} className="text-blue-400" />
            <h1 className="text-3xl font-bold">Privacy Policy</h1>
          </div>
          <p className="text-gray-300 text-sm">
            {PLATFORM_NAME} · Last Updated: {LAST_UPDATED}
          </p>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-6 py-12">
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-8 space-y-10">

          {/* Intro */}
          <section>
            <p className="text-gray-700 leading-relaxed text-base">
              {COMPANY_NAME} ("we," "us," or "our") operates <strong>{PLATFORM_NAME}</strong> ({PLATFORM_URL}), a
              multi-tenant appointment booking platform for aesthetic clinics and med spas (the "Platform"). This
              Privacy Policy explains how we collect, use, disclose, and protect information when you use the Platform,
              including as a business owner (tenant), staff member, or end client booking an appointment.
            </p>
            <p className="text-gray-700 leading-relaxed mt-4">
              By accessing or using the Platform, you agree to the collection and use of information described in
              this policy. If you do not agree, please discontinue use of the Platform.
            </p>
          </section>

          <Divider />

          {/* 1. Information We Collect */}
          <section>
            <SectionHeader icon={<Database size={20} />} number="1" title="Information We Collect" />

            <Subsection title="1.1 Information You Provide Directly">
              <ul className="list-disc list-inside space-y-2 text-gray-700">
                <li><strong>Account Registration:</strong> Business name, email address, password, phone number, and business address when a tenant account is created.</li>
                <li><strong>Business Settings:</strong> Logo, brand colors, business hours, cancellation policies, and payment configurations submitted by tenant administrators.</li>
                <li><strong>Team Members:</strong> Names, email addresses, and role assignments for staff invited to a tenant account.</li>
                <li><strong>Client Profiles:</strong> Name, email address, and phone number entered by tenants on behalf of their clients for appointment booking purposes.</li>
                <li><strong>Appointment Details:</strong> Service selections, provider assignments, dates, times, and internal appointment notes.</li>
                <li><strong>Intake & Consent Forms:</strong> Completed form responses and electronic signature data (drawn or typed) submitted by clients prior to appointments.</li>
                <li><strong>Communications:</strong> Messages or support inquiries submitted to us.</li>
              </ul>
            </Subsection>

            <Subsection title="1.2 Information Collected Automatically">
              <ul className="list-disc list-inside space-y-2 text-gray-700">
                <li><strong>Usage Data:</strong> Pages visited, features used, and interaction logs for analytics and platform improvement.</li>
                <li><strong>Device & Browser Information:</strong> IP address, browser type, operating system, and referring URL.</li>
                <li><strong>Cookies & Local Storage:</strong> Session tokens and user preferences stored locally to maintain authentication state. We do not use tracking cookies for advertising.</li>
              </ul>
            </Subsection>

            <Subsection title="1.3 Payment Information">
              <p className="text-gray-700">
                We process payments through <strong>Stripe</strong> using Stripe Connect. We do not store full credit card
                numbers or CVV codes. Payment method tokens, transaction IDs, and charge records are stored in your tenant
                account. All payment data in transit is encrypted via TLS. Stripe's privacy policy governs their handling
                of payment card data: <a href="https://stripe.com/privacy" className="text-blue-600 underline" target="_blank" rel="noopener noreferrer">stripe.com/privacy</a>.
              </p>
            </Subsection>

            <Subsection title="1.4 SMS Consent & Communications">
              <p className="text-gray-700">
                When clients book an appointment and opt in to SMS notifications, we collect and store:
              </p>
              <ul className="list-disc list-inside space-y-2 text-gray-700 mt-2">
                <li>The client's mobile phone number</li>
                <li>SMS opt-in consent status and timestamp</li>
                <li>Opt-out (STOP) requests and timestamp</li>
                <li>A log of all SMS messages sent and their delivery status</li>
              </ul>
              <p className="text-gray-700 mt-3">
                SMS messages are delivered via <strong>Telnyx, Inc.</strong> Telnyx's privacy policy governs their
                handling of message routing data: <a href="https://telnyx.com/privacy-policy" className="text-blue-600 underline" target="_blank" rel="noopener noreferrer">telnyx.com/privacy-policy</a>.
              </p>
            </Subsection>
          </section>

          <Divider />

          {/* 2. How We Use Your Information */}
          <section>
            <SectionHeader icon={<Eye size={20} />} number="2" title="How We Use Your Information" />
            <ul className="list-disc list-inside space-y-2 text-gray-700">
              <li>To create and manage tenant accounts and staff profiles</li>
              <li>To provide the appointment booking, scheduling, and management features of the Platform</li>
              <li>To send transactional SMS and email notifications (booking confirmations, appointment reminders, cancellation notices)</li>
              <li>To process and record payments and issue refunds via Stripe</li>
              <li>To generate and deliver intake and consent forms to clients</li>
              <li>To power the AI-assisted booking chatbot using conversation context scoped to the tenant's business data</li>
              <li>To generate business analytics and performance reports for tenant owners</li>
              <li>To facilitate post-visit satisfaction ratings and route positive reviews to public platforms (Google, Yelp) as configured by the tenant</li>
              <li>To enforce platform security, detect fraud, and prevent abuse</li>
              <li>To comply with applicable laws and legal obligations</li>
              <li>To improve the Platform's features and performance</li>
            </ul>
          </section>

          <Divider />

          {/* 3. SMS Program Details */}
          <section>
            <SectionHeader icon={<MessageSquare size={20} />} number="3" title="SMS Messaging Program" />

            <div className="bg-blue-50 border border-blue-200 rounded-xl p-5 mb-5">
              <p className="text-blue-900 font-medium text-sm">
                This section applies to all SMS messages sent through the {PLATFORM_NAME} Platform.
              </p>
            </div>

            <Subsection title="3.1 Program Description">
              <p className="text-gray-700">
                {PLATFORM_NAME} sends appointment-related SMS messages from telephone numbers registered to our platform.
                Messages are strictly transactional — they relate directly to appointments booked through the Platform.
              </p>
            </Subsection>

            <Subsection title="3.2 Types of Messages Sent">
              <ul className="list-disc list-inside space-y-2 text-gray-700">
                <li><strong>Booking Confirmation:</strong> Sent immediately after an appointment is successfully booked. Includes service name, provider, date, time, location, and confirmation code.</li>
                <li><strong>24-Hour Reminder:</strong> Sent approximately 24 hours before a scheduled appointment.</li>
                <li><strong>1-Hour Reminder:</strong> Sent approximately 1 hour before a scheduled appointment.</li>
                <li><strong>Cancellation Notice:</strong> Sent when an appointment is cancelled by staff or the client.</li>
                <li><strong>Reschedule Confirmation:</strong> Sent when an appointment is rescheduled, with updated details.</li>
                <li><strong>Post-Visit Satisfaction Request:</strong> A single message after appointment completion asking the client to rate their experience (if enabled by the tenant).</li>
              </ul>
            </Subsection>

            <Subsection title="3.3 Message Frequency">
              <p className="text-gray-700">
                Message frequency varies based on your appointment schedule. Clients can expect approximately
                <strong> 2–4 messages per scheduled appointment</strong>. No marketing, promotional, or unsolicited messages
                are sent through this program.
              </p>
            </Subsection>

            <Subsection title="3.4 How to Opt Out">
              <p className="text-gray-700">
                You may opt out of SMS notifications at any time by replying <strong>STOP</strong> to any message received
                from us. After opting out, you will receive a single confirmation message and no further SMS messages will
                be sent. To re-enroll, you may opt in again during a future booking.
              </p>
              <p className="text-gray-700 mt-2">
                You may also contact the business directly or email us at{" "}
                <a href={`mailto:${CONTACT_EMAIL}`} className="text-blue-600 underline">{CONTACT_EMAIL}</a> to opt out.
              </p>
            </Subsection>

            <Subsection title="3.5 Help">
              <p className="text-gray-700">
                Reply <strong>HELP</strong> to any message for assistance. Standard message and data rates may apply based
                on your mobile carrier and plan.
              </p>
            </Subsection>

            <Subsection title="3.6 Consent Requirement">
              <p className="text-gray-700">
                SMS messages are only sent to clients who have explicitly opted in by checking the SMS consent checkbox
                during the booking process. Consent is never required as a condition of booking an appointment.
                Consent records are maintained with timestamp and associated phone number.
              </p>
            </Subsection>

            <Subsection title="3.7 No Third-Party Sharing for Marketing">
              <p className="text-gray-700">
                <strong>We do not sell, rent, or share your mobile phone number with third parties for marketing
                purposes.</strong> Phone numbers are used solely to deliver the appointment-related SMS messages
                described in Section 3.2.
              </p>
            </Subsection>
          </section>

          <Divider />

          {/* 4. How We Share Information */}
          <section>
            <SectionHeader icon={<Lock size={20} />} number="4" title="How We Share Information" />

            <p className="text-gray-700 mb-4">
              We do not sell personal information. We share information only in the following circumstances:
            </p>

            <Subsection title="4.1 Service Providers">
              <p className="text-gray-700">We use the following trusted third-party service providers to operate the Platform:</p>
              <div className="overflow-x-auto mt-3">
                <table className="w-full text-sm text-gray-700 border border-gray-200 rounded-lg overflow-hidden">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="text-left p-3 font-semibold">Provider</th>
                      <th className="text-left p-3 font-semibold">Purpose</th>
                      <th className="text-left p-3 font-semibold">Data Shared</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    <tr><td className="p-3 font-medium">Telnyx, Inc.</td><td className="p-3">SMS delivery</td><td className="p-3">Phone numbers, message content</td></tr>
                    <tr><td className="p-3 font-medium">Resend / SendGrid</td><td className="p-3">Transactional email</td><td className="p-3">Email addresses, email content</td></tr>
                    <tr><td className="p-3 font-medium">Stripe, Inc.</td><td className="p-3">Payment processing</td><td className="p-3">Payment method data, transaction amounts</td></tr>
                    <tr><td className="p-3 font-medium">Neon / PostgreSQL</td><td className="p-3">Database hosting</td><td className="p-3">All stored platform data (encrypted at rest)</td></tr>
                    <tr><td className="p-3 font-medium">Cloudflare, Inc.</td><td className="p-3">CDN, edge computing, Workers hosting</td><td className="p-3">Request metadata, IP addresses</td></tr>
                    <tr><td className="p-3 font-medium">Anthropic, PBC</td><td className="p-3">AI chatbot fallback (Claude Haiku)</td><td className="p-3">Anonymized conversation text scoped to tenant context</td></tr>
                  </tbody>
                </table>
              </div>
              <p className="text-gray-600 text-sm mt-3">
                Each provider is bound by their own privacy policies and, where applicable, data processing agreements.
              </p>
            </Subsection>

            <Subsection title="4.2 Tenant Businesses">
              <p className="text-gray-700">
                When you book an appointment through the Platform, your personal information (name, email, phone, appointment
                details) is accessible to the tenant business you booked with. Each tenant's data is isolated in a
                dedicated database and is not shared with other tenants.
              </p>
            </Subsection>

            <Subsection title="4.3 Legal Requirements">
              <p className="text-gray-700">
                We may disclose information if required to do so by law, subpoena, court order, or other governmental
                authority, or if we believe in good faith that disclosure is necessary to protect our rights, protect your
                safety or the safety of others, investigate fraud, or respond to a government request.
              </p>
            </Subsection>

            <Subsection title="4.4 Business Transfers">
              <p className="text-gray-700">
                In the event of a merger, acquisition, or sale of all or a portion of our assets, personal information
                may be transferred. We will notify affected users via email or platform notice prior to any such transfer.
              </p>
            </Subsection>
          </section>

          <Divider />

          {/* 5. Data Security */}
          <section>
            <SectionHeader icon={<Lock size={20} />} number="5" title="Data Security" />
            <p className="text-gray-700 mb-4">
              We implement industry-standard technical and organizational measures to protect your data:
            </p>
            <ul className="list-disc list-inside space-y-2 text-gray-700">
              <li><strong>Per-Tenant Database Isolation:</strong> Each tenant's client data resides in a dedicated, logically isolated database. No cross-tenant data access is possible at the application layer.</li>
              <li><strong>Encryption in Transit:</strong> All data transmitted between clients, the Platform, and service providers is encrypted using TLS 1.2 or higher.</li>
              <li><strong>Encryption at Rest:</strong> All databases are encrypted at rest by the hosting provider (Neon/PostgreSQL).</li>
              <li><strong>Authentication:</strong> Short-lived JSON Web Tokens (15-minute access tokens, 7-day refresh tokens) with PBKDF2 password hashing.</li>
              <li><strong>Rate Limiting:</strong> Authentication endpoints are rate-limited to prevent brute-force attacks.</li>
              <li><strong>Secrets Management:</strong> API keys and credentials are stored in Cloudflare's encrypted secrets vault — never in source code.</li>
            </ul>
            <p className="text-gray-700 mt-4">
              No method of transmission over the internet is 100% secure. While we strive to protect your data, we cannot
              guarantee absolute security. In the event of a data breach that affects your personal information, we will
              notify you in accordance with applicable law.
            </p>
          </section>

          <Divider />

          {/* 6. Data Retention */}
          <section>
            <SectionHeader icon={<Trash2 size={20} />} number="6" title="Data Retention" />
            <ul className="list-disc list-inside space-y-2 text-gray-700">
              <li><strong>Active Accounts:</strong> Data is retained for as long as your account remains active.</li>
              <li><strong>Tenant Cancellation:</strong> Upon cancellation of a tenant account, we will retain data for 30 days to allow for recovery, then delete it upon request or within 90 days.</li>
              <li><strong>Client Records:</strong> Client appointment history, forms, and notes are retained for the duration of the tenant's active subscription to support ongoing care continuity.</li>
              <li><strong>SMS Logs:</strong> SMS delivery logs are retained for 12 months and then deleted or anonymized.</li>
              <li><strong>Payment Records:</strong> Transaction records are retained for 7 years to comply with financial record-keeping requirements.</li>
              <li><strong>Audit Logs:</strong> Platform-level audit logs are retained for 24 months.</li>
            </ul>
          </section>

          <Divider />

          {/* 7. Your Rights */}
          <section>
            <SectionHeader icon={<Eye size={20} />} number="7" title="Your Privacy Rights" />
            <p className="text-gray-700 mb-4">
              Depending on your jurisdiction, you may have the following rights regarding your personal information:
            </p>
            <ul className="list-disc list-inside space-y-2 text-gray-700">
              <li><strong>Access:</strong> Request a copy of the personal data we hold about you.</li>
              <li><strong>Correction:</strong> Request correction of inaccurate or incomplete data.</li>
              <li><strong>Deletion:</strong> Request deletion of your personal data, subject to legal retention requirements.</li>
              <li><strong>Portability:</strong> Request your data in a portable, machine-readable format.</li>
              <li><strong>Opt-Out of SMS:</strong> Reply STOP to any message, or contact us directly at any time.</li>
              <li><strong>Opt-Out of Email:</strong> Use the unsubscribe link in any email or contact us at {CONTACT_EMAIL}.</li>
            </ul>
            <p className="text-gray-700 mt-4">
              To exercise any of these rights, contact us at{" "}
              <a href={`mailto:${CONTACT_EMAIL}`} className="text-blue-600 underline">{CONTACT_EMAIL}</a>.
              We will respond within 30 days.
            </p>
            <p className="text-gray-700 mt-3 text-sm">
              <strong>California Residents:</strong> Under the California Consumer Privacy Act (CCPA), California residents
              have additional rights including the right to know what personal information is sold or disclosed for business
              purposes. We do not sell personal information. For CCPA inquiries, contact {CONTACT_EMAIL}.
            </p>
          </section>

          <Divider />

          {/* 8. Cookies */}
          <section>
            <SectionHeader icon={<Database size={20} />} number="8" title="Cookies & Local Storage" />
            <p className="text-gray-700">
              The Platform uses browser local storage and session storage to maintain your authentication state (JWT tokens)
              and user preferences (theme, display settings). We do not use third-party advertising cookies. Analytics are
              limited to Cloudflare's privacy-preserving edge analytics, which do not track individuals across websites.
            </p>
          </section>

          <Divider />

          {/* 9. Children */}
          <section>
            <SectionHeader icon={<Shield size={20} />} number="9" title="Children's Privacy" />
            <p className="text-gray-700">
              The Platform is not directed at children under the age of 13. We do not knowingly collect personal information
              from children under 13. If you believe we have inadvertently collected such information, please contact us
              immediately at {CONTACT_EMAIL} and we will delete it promptly.
            </p>
          </section>

          <Divider />

          {/* 10. Changes */}
          <section>
            <SectionHeader icon={<Eye size={20} />} number="10" title="Changes to This Policy" />
            <p className="text-gray-700">
              We may update this Privacy Policy from time to time to reflect changes in our practices, technology, legal
              requirements, or business operations. We will notify you of material changes by updating the "Last Updated"
              date above and, where appropriate, by sending an email notice to tenant account holders. Continued use of
              the Platform after changes become effective constitutes acceptance of the updated policy.
            </p>
          </section>

          <Divider />

          {/* 11. Contact */}
          <section>
            <SectionHeader icon={<Mail size={20} />} number="11" title="Contact Us" />
            <p className="text-gray-700 mb-4">
              For privacy-related questions, requests, or concerns, please contact us:
            </p>
            <div className="bg-gray-50 rounded-xl p-5 space-y-2 text-gray-700">
              <p><strong>{COMPANY_NAME}</strong></p>
              <p>Operating as: {PLATFORM_NAME}</p>
              <p>
                <Mail size={15} className="inline mr-2" />
                <a href={`mailto:${CONTACT_EMAIL}`} className="text-blue-600 underline">{CONTACT_EMAIL}</a>
              </p>
              <p>
                <Phone size={15} className="inline mr-2" />
                For SMS opt-out assistance, reply STOP to any message
              </p>
              <p>
                Website:{" "}
                <a href={PLATFORM_URL} className="text-blue-600 underline" target="_blank" rel="noopener noreferrer">
                  {PLATFORM_URL}
                </a>
              </p>
            </div>
          </section>

        </div>

        {/* Footer */}
        <div className="mt-8 text-center text-sm text-gray-500">
          <p>© {new Date().getFullYear()} {COMPANY_NAME} · All rights reserved</p>
          <p className="mt-1">
            <a href="/terms" className="text-blue-600 underline mr-4">Terms of Service</a>
            <a href={PLATFORM_URL} className="text-blue-600 underline">AppointmentFlo.com</a>
          </p>
        </div>
      </main>
    </div>
  );
}

function SectionHeader({ icon, number, title }: { icon: React.ReactNode; number: string; title: string }) {
  return (
    <div className="flex items-center gap-3 mb-5">
      <div className="w-9 h-9 bg-[#273240] rounded-lg flex items-center justify-center text-white flex-shrink-0">
        {icon}
      </div>
      <h2 className="text-xl font-bold text-gray-900">{number}. {title}</h2>
    </div>
  );
}

function Subsection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="mt-5">
      <h3 className="text-base font-semibold text-gray-800 mb-2">{title}</h3>
      {children}
    </div>
  );
}

function Divider() {
  return <hr className="border-gray-100" />;
}
